import { useAnalytics } from '@hooks/useAnalytics';

import { useNavigate, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { openExternalUrl } from '@utilities/openExternalUrl';

import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { SelectEntryPoint } from '@screens/booking/BookingWizardScreen/BookingWizardScreen';

import worldPrideBanner from '../world-pride-banner.png';

const LiaisonCard = () => {
  const { navigate } = useRouter();
  const dylanProfile: { to: string; title: string; image: string } = {
    to: 'https://www.instagram.com/nothisisdylan/',
    title: 'Dylan Dickherson',
    image: 'https://gaggl.app/api/public/uploads/2025/5/7hriciqzxa6.jpg',
  };

  return (
    <>
      <TitleToolbar text="Drag Event Liaison" size="sm" />
      <div
        className="relative"
        style={{
          background: 'rgba(255,255,255,.1)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
        onClick={
          dylanProfile?.to
            ? () => {
                navigate(dylanProfile.to);
              }
            : undefined
        }
      >
        <div className="d-flex gap-3 align-items-center">
          <div className="flex-shrink-0">
            <div style={{ width: 60, height: 60 }}>
              {dylanProfile?.image ? (
                <img
                  src={dylanProfile?.image}
                  alt={dylanProfile?.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '.1rem ',
                opacity: 0.5,
              }}
            >
              <strong>DC/Baltimore Metro Area</strong>
            </div>
            <div className="fs-6">
              <strong>{dylanProfile?.title}</strong>
            </div>
            <div className="opacity-75"></div>
          </div>
          <div className="ml-auto px-3">
            {dylanProfile?.to && <FontAwesomeIcon name="chevron-right" />}
          </div>
        </div>
      </div>
    </>
  );
};

export const WorldPrideFormsScreen = createScreenComponent(() => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const { createAuthGateHandler } = useAuthGate();

  const handleManageEvents = () => {
    track('Share World Pride Event');

    navigate('/noauth/world-pride/manage-shows', {
      state: { back: '/noauth/world-pride' },
    });
  };

  const handleCreateProfile = () => {
    track('Create World Pride Entertainer Profile');

    createAuthGateHandler(
      () => {
        openExternalUrl(
          'https://docs.google.com/forms/d/e/1FAIpQLScns3Miiv7JPB56gBXotP17eOH1ZW7PuyVk42_uGMPn0I2wMw/viewform?usp=header',
        );
      },
      {
        entertainerCallback: () => {},
      },
    );
  };

  return (
    <Screen
      name="World Pride Forms Screen"
      hideToolbar
      disableLocationPermissionGate
      headerProps={{
        title: <Screen.GagglTitle />,
        right: (
          <ShareLinkButton
            url={`https://gaggl.app/world-pride`}
            track={{ event: 'Share World Pride Link' }}
          />
        ),
      }}
    >
      <Screen.Content>
        <img
          src={worldPrideBanner}
          alt="World Pride 2025"
          style={{ width: '100%', borderRadius: 8 }}
        />
        <h1 style={{ fontSize: '1.8rem', margin: 0 }}>World Pride Drag 2025</h1>
        <p className="mt-0">
          We're gathering information on local drag in the DC/Baltimore area ahead of World Pride.
        </p>
        <p>
          If you're producing a show or performing during Pride season, let us know by filling out
          one of the forms below:
        </p>
        <SelectEntryPoint
          listItems={[
            {
              title: 'Share a World Pride Drag Event',
              subTitle:
                'Let us know about any drag shows happening in May or June 2025 in the DC/Baltimore area.',
              onClick: handleManageEvents,
            },
            {
              title: 'Create Your Entertainer Profile',
              subTitle:
                "Whether you're local or traveling in for Pride, add your performer info so we can help get the word out.",
              onClick: handleCreateProfile,
            },
          ]}
        />
        <LiaisonCard />
      </Screen.Content>
    </Screen>
  );
});
